import React, { useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { fmtEur, fmtNum } from "@/components/ui-kit";
import { RefreshCw, Printer, Upload, UserCheck, X, Trash2, CheckCircle2, AlertTriangle } from "lucide-react";
import { tipoCostoInfo } from "@/lib/tipoCosto";
import { giudizioScarto } from "@/components/commessa/ArtigianiAI";
import { AffidaDialog } from "@/components/commessa/AffidaDialog";
import { PreventivoRicevutoDialog } from "@/components/commessa/PreventivoRicevutoDialog";
import { costoReale, scartoPct, soggettoLabel } from "@/lib/margineCommessa";

const FILTRI = [["tutte", "Tutte"], ["da_affidare", "Da affidare"], ["affidate", "Affidate"]];

function Kpi({ label, value, sub, tone = "" }) {
  return (
    <div className="bg-white border border-zinc-200 rounded p-3">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`text-lg font-bold mono ${tone}`}>{value}</div>
      {sub && <div className="text-[11px] text-zinc-500">{sub}</div>}
    </div>
  );
}

function RigaVoce({ it, sel, onSel, cid, reload }) {
  const t = tipoCostoInfo(it.tipo_costo);
  const reale = costoReale(it);
  const pct = scartoPct(it);
  const g = giudizioScarto(pct);
  const [val, setVal] = useState(reale ?? "");
  const salvaCosto = async () => {
    if (String(val) === String(reale ?? "")) return;
    try {
      await api.patch(`/commesse/${cid}/workflow/computo-items`, { updates: [{ id: it.id, costo_effettivo: val === "" ? null : Number(val) }] });
      toast.success("Costo reale aggiornato"); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Salvataggio non riuscito"); }
  };
  const togli = async () => {
    try { await api.post(`/commesse/${cid}/workflow/affida/rimuovi`, { item_ids: [it.id] }); toast.success("Affidamento tolto"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Operazione non riuscita"); }
  };
  return (
    <tr className={`border-t ${it.affidamento ? "" : "bg-amber-50/40"}`} data-testid={`ca-riga-${it.id}`}>
      <td className="px-2 py-1.5"><input type="checkbox" checked={sel} onChange={onSel} data-testid={`ca-sel-${it.id}`} /></td>
      <td className="px-2 py-1.5">
        <div className="font-medium">{it.name}</div>
        <div className="flex gap-1 mt-0.5 flex-wrap">
          <span className={`text-[9px] px-1.5 rounded ${t.cls}`}>{t.short}</span>
          {it.origine === "extra" && <span className="text-[9px] px-1.5 rounded bg-violet-100 text-violet-700">extra {it.preventivo_numero || ""}</span>}
          {!Number(it.costo_unit_previsto) && <span className="text-[9px] px-1.5 rounded bg-rose-100 text-rose-700" title="Imposta il prezzo d'acquisto della voce in Voci Backoffice">costo d'acquisto mancante</span>}
        </div>
      </td>
      <td className="px-2 text-right mono whitespace-nowrap">{fmtNum(it.qty, 2)} {it.unit}</td>
      <td className="px-2 text-right mono">{fmtEur(it.totale)}</td>
      <td className="px-2 text-right mono">{fmtEur(it.costo_previsto)}</td>
      <td className="px-2">
        {it.affidamento ? (
          <div className="text-xs"><div className="font-semibold">{it.affidamento.nome}</div><div className="text-[10px] text-zinc-500">{soggettoLabel(it.affidamento.tipo)}{it.affidamento.fonte === "preventivo" ? " · da preventivo" : ""}{it.affidamento.autorizzato === false ? " · da autorizzare" : ""}</div></div>
        ) : <span className="text-[11px] text-amber-700 font-semibold">da affidare</span>}
      </td>
      <td className="px-2 py-1">
        {it.affidamento ? <Input type="number" value={val} onChange={(e) => setVal(e.target.value)} onBlur={salvaCosto} placeholder="= previsto" className="h-7 text-xs text-right w-24 ml-auto" data-testid={`ca-costo-${it.id}`} /> : <span className="text-[11px] text-zinc-400 block text-right">—</span>}
      </td>
      <td className="px-2 text-center">{reale != null && <span className={`px-1.5 py-0.5 rounded text-[10px] ${g.cls}`}>{pct == null ? g.txt : `${pct > 0 ? "+" : ""}${pct.toFixed(0)}%`}</span>}</td>
      <td className="px-2 text-right">{it.affidamento && <button onClick={togli} className="p-1 rounded hover:bg-rose-50 text-rose-600" title="Togli affidamento" data-testid={`ca-togli-${it.id}`}><X className="h-3.5 w-3.5" /></button>}</td>
    </tr>
  );
}

function PreventiviRicevuti({ wf, cid, reload }) {
  const rows = wf.artigiani_preventivi || [];
  if (!rows.length) return null;
  const del = async (p) => {
    if (!window.confirm(`Eliminare il preventivo di ${p.artigiano_nome}? Le voci che copriva torneranno da affidare.`)) return;
    try { await api.delete(`/commesse/${cid}/workflow/artigiani-preventivi/${p.id}`); toast.success("Eliminato"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Operazione non riuscita"); }
  };
  const autorizza = async (p) => {
    try { await api.post(`/commesse/${cid}/workflow/artigiani-preventivi/${p.id}/autorizza`); toast.success("Autorizzato"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Operazione non riuscita"); }
  };
  return (
    <div className="bg-white border border-zinc-200 rounded" data-testid="ca-preventivi-ricevuti">
      <div className="px-4 py-2 border-b text-sm font-semibold">Preventivi ricevuti ({rows.length})</div>
      <table className="w-full text-xs">
        <tbody>
          {rows.map((p) => {
            const g = giudizioScarto(p.confronto_totali?.scarto_pct ?? p.ai_analisi?.scarto_pct_su_acquisto);
            return (
              <tr key={p.id} className="border-t" data-testid={`ca-pr-${p.id}`}>
                <td className="px-3 py-2"><div className="font-semibold">{p.artigiano_nome}</div><div className="text-[10px] text-zinc-500">{soggettoLabel(p.tipo_soggetto)} · {(p.affida_righe || []).length} voci coperte{p.extra_non_previsto ? ` · ${fmtEur(p.extra_non_previsto)} non previsti` : ""}</div></td>
                <td className="px-3 text-right mono">{fmtEur(p.importo_offerto)}</td>
                <td className="px-3"><span className={`px-1.5 py-0.5 rounded text-[10px] ${g.cls}`}>{g.txt}</span></td>
                <td className="px-3 text-[10px]">{p.stato === "da_autorizzare" ? <span className="text-rose-700 font-semibold">da autorizzare</span> : p.stato}</td>
                <td className="px-3 text-right whitespace-nowrap">
                  {p.stato === "da_autorizzare" && <Button size="sm" variant="outline" className="h-7 text-xs mr-1" onClick={() => autorizza(p)} data-testid={`ca-pr-auth-${p.id}`}><CheckCircle2 className="h-3.5 w-3.5 mr-1" />Autorizza</Button>}
                  <button onClick={() => del(p)} className="p-1 rounded hover:bg-rose-50 text-rose-600" data-testid={`ca-pr-del-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// Unica schermata: voci dei preventivi della commessa → affidamento (preventivo ricevuto o prezzo manuale) → costo reale e margine.
export default function ComputoAffidamenti({ wf, cid, reload, vediDettagli }) {
  const items = useMemo(() => wf.computo_metrico?.items || [], [wf]);
  const marg = wf.marginalita || {};
  const [filtro, setFiltro] = useState("tutte");
  const [sel, setSel] = useState([]);
  const [affida, setAffida] = useState(false);
  const [ricevuto, setRicevuto] = useState(false);
  const vis = items.filter((it) => filtro === "tutte" || (filtro === "da_affidare" ? !it.affidamento : !!it.affidamento));
  const selItems = items.filter((it) => sel.includes(it.id));
  const tog = (id) => setSel(sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]);
  const allVis = vis.length > 0 && vis.every((it) => sel.includes(it.id));
  const daAff = items.filter((it) => !it.affidamento);
  const stampa = () => {
    const ids = (selItems.length ? selItems : daAff).map((i) => i.id);
    if (!ids.length) return toast.error("Nessuna voce da stampare");
    window.open(`/commesse/${cid}/stampa/computo?ids=${ids.join(",")}`, "_blank");
  };
  const riallinea = async () => {
    try { await api.post(`/commesse/${cid}/workflow/computo`); toast.success("Computo riallineato ai preventivi"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Operazione non riuscita"); }
  };
  return (
    <div className="space-y-3" data-testid="computo-affidamenti">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <Kpi label="Voci preventivate" value={items.length} sub={`${(wf.preventivi || []).length} preventivi collegati`} />
        <Kpi label="Da affidare" value={daAff.length} sub={fmtEur(daAff.reduce((s, it) => s + (Number(it.costo_previsto) || 0), 0)) + " previsti"} tone={daAff.length ? "text-amber-600" : "text-emerald-600"} />
        <Kpi label="Costo previsto" value={fmtEur(marg.costo_previsto)} sub="prezzi d'acquisto" />
        <Kpi label="Costo reale/attuale" value={fmtEur(marg.costo_reale_voci)} sub={marg.extra_artigiani ? `+ ${fmtEur(marg.extra_artigiani)} extra non previsti` : "previsto dove manca il reale"} tone={marg.costo_reale_voci > marg.costo_previsto ? "text-rose-600" : "text-emerald-600"} />
        {vediDettagli && <Kpi label="Scostamento" value={fmtEur((marg.costo_reale_voci || 0) + (marg.extra_artigiani || 0) - (marg.costo_previsto || 0))} sub="reale − previsto" tone={(marg.costo_reale_voci || 0) + (marg.extra_artigiani || 0) > (marg.costo_previsto || 0) ? "text-rose-600" : "text-emerald-600"} />}
      </div>
      <div className="flex items-center gap-2 flex-wrap bg-white border border-zinc-200 rounded p-2">
        {FILTRI.map(([k, l]) => <button key={k} onClick={() => setFiltro(k)} className={`px-3 py-1 rounded text-xs border ${filtro === k ? "bg-zinc-900 text-white border-zinc-900" : "bg-white border-zinc-300"}`} data-testid={`ca-filtro-${k}`}>{l}</button>)}
        <div className="flex-1" />
        <span className="text-xs text-zinc-500" data-testid="ca-sel-count">{sel.length} selezionate</span>
        <Button size="sm" variant="outline" onClick={() => setRicevuto(true)} data-testid="ca-carica-prev"><Upload className="h-4 w-4 mr-1" />Carica preventivo ricevuto</Button>
        <Button size="sm" variant="outline" disabled={!sel.length} onClick={() => setAffida(true)} data-testid="ca-affida"><UserCheck className="h-4 w-4 mr-1" />Affida / prezza selezionate</Button>
        <Button size="sm" variant="outline" onClick={stampa} data-testid="ca-stampa" title="Computo senza prezzi da mandare all'artigiano"><Printer className="h-4 w-4 mr-1" />Computo senza prezzi{sel.length ? ` (${sel.length})` : " (da affidare)"}</Button>
        <Button size="sm" variant="ghost" onClick={riallinea} title="Riallinea alle ultime modifiche dei preventivi" data-testid="ca-riallinea"><RefreshCw className="h-4 w-4" /></Button>
      </div>
      {!items.length && <div className="bg-amber-50 border border-amber-300 rounded p-3 text-sm text-amber-800"><AlertTriangle className="inline h-4 w-4 mr-1" />Il preventivo collegato non ha voci con quantità o prezzo.</div>}
      {items.length > 0 && (
        <div className="bg-white border border-zinc-200 rounded overflow-x-auto">
          <table className="w-full text-sm min-w-[960px]">
            <thead className="bg-zinc-50 text-[10px] uppercase tracking-widest text-zinc-500"><tr>
              <th className="px-2 py-2"><input type="checkbox" checked={allVis} onChange={() => setSel(allVis ? sel.filter((id) => !vis.some((v) => v.id === id)) : [...new Set([...sel, ...vis.map((v) => v.id)])])} data-testid="ca-sel-all" /></th>
              <th className="px-2 text-left">Voce</th><th className="px-2 text-right">Q.tà</th><th className="px-2 text-right">Venduto</th><th className="px-2 text-right">Costo previsto</th>
              <th className="px-2 text-left">Affidata a</th><th className="px-2 text-right">Costo reale</th><th className="px-2">Scarto</th><th />
            </tr></thead>
            <tbody>{vis.map((it) => <RigaVoce key={it.id + String(it.affidamento?.costo_effettivo)} it={it} sel={sel.includes(it.id)} onSel={() => tog(it.id)} cid={cid} reload={reload} />)}</tbody>
          </table>
        </div>
      )}
      <PreventiviRicevuti wf={wf} cid={cid} reload={reload} />
      <AffidaDialog open={affida} onOpenChange={setAffida} cid={cid} items={selItems} onDone={() => { setSel([]); reload(); }} />
      <PreventivoRicevutoDialog open={ricevuto} onOpenChange={setRicevuto} cid={cid} items={items} preselezione={sel} onDone={() => { setSel([]); reload(); }} />
    </div>
  );
}
